import type { ProcessingJob } from "features/dt-plus-setup/model/types";

import { CustomModal } from "components/CustomModal";
import { NavigationTabs, type Tab } from "components/NavigationTabs";
import { ExecutionStepsContent } from "features/dt-plus-setup/components/steps/ExecutionStepsContent";
import { isTerminalJobStatus, toTrimmedDetailText } from "features/dt-plus-setup/lib/processingJob";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useJobLogs } from "../hooks/useJobLogs";
import { JOB_DETAILS_TAB } from "../model/constants";
import { TransientJobConfigSummary } from "./TransientJobConfigSummary";
import { TransientJobInputs } from "./TransientJobInputs";
import { TransientJobLogs } from "./TransientJobLogs";
import { TransientJobResults } from "./TransientJobResults";

type TransientJobDetailsModalProps = {
  job: null | ProcessingJob;
  onClose: () => void;
};

export const TransientJobDetailsModal = ({ job, onClose }: TransientJobDetailsModalProps) => {
  const { t } = useTranslation();
  const [activeTabName, setActiveTabName] = useState(JOB_DETAILS_TAB.logs);
  const configId = job ? job.config_id : undefined;
  const shouldRefresh = Boolean(job && !isTerminalJobStatus(job.status));

  const { bottomRef, error, isStreaming, lines } = useJobLogs(job, Boolean(job), configId, shouldRefresh);
  const tabs: Tab[] = [
    { displayName: t("Execution.transient.jobDetails.tabs.state"), name: JOB_DETAILS_TAB.state },
    { displayName: t("Execution.transient.jobDetails.tabs.logs"), name: JOB_DETAILS_TAB.logs },
    { displayName: t("Execution.transient.jobDetails.tabs.result"), name: JOB_DETAILS_TAB.result },
    { displayName: t("Execution.transient.jobDetails.tabs.input"), name: JOB_DETAILS_TAB.input },
    { displayName: t("Execution.transient.jobDetails.tabs.config"), name: JOB_DETAILS_TAB.config },
  ];
  const logsTab = tabs.find((tab) => tab.name === JOB_DETAILS_TAB.logs) ?? tabs[0];
  const activeTab = tabs.find((tab) => tab.name === activeTabName) ?? logsTab;

  useEffect(() => {
    setActiveTabName(JOB_DETAILS_TAB.logs);
  }, [job?.job_id]);

  return (
    <CustomModal
      className="overflow-hidden"
      height="90vh"
      modalOpen={job != null}
      padding="0.5rem 2.4rem 2.4rem 2.4rem"
      setModalOpen={(value: boolean) => {
        if (!value) onClose();
      }}
      width="96vw"
    >
      <div className="h-full overflow-auto text-left">
        <h2 className="font-ds-headline-lg mb-3 flex h-[3.2rem] items-center pr-10">
          {t("Execution.transient.jobDetails.title")}
        </h2>
        <NavigationTabs
          activeTab={activeTab}
          className="mb-3"
          setActiveTab={(tab) => setActiveTabName(tab.name)}
          tabs={tabs}
        />
        {job && activeTabName === JOB_DETAILS_TAB.state ? (
          <ExecutionStepsContent
            errorMessage={toTrimmedDetailText(job.error_details) || undefined}
            executedAt={job.executed_at}
            infoMessage={toTrimmedDetailText(job.details?.summary) || undefined}
            progressPercent={job.progress_percent}
            showHeader={false}
            status={job.status}
            steps={job.steps}
          />
        ) : null}
        {job && activeTabName === JOB_DETAILS_TAB.config ? <TransientJobConfigSummary job={job} /> : null}
        {job && activeTabName === JOB_DETAILS_TAB.logs ? (
          <TransientJobLogs
            bottomRef={bottomRef}
            configId={configId}
            error={error}
            isStreaming={isStreaming}
            lines={lines}
          />
        ) : null}
        {job && activeTabName === JOB_DETAILS_TAB.result ? (
          <TransientJobResults configId={configId} shouldRefresh={shouldRefresh} />
        ) : null}
        {job && activeTabName === JOB_DETAILS_TAB.input ? (
          <TransientJobInputs configId={configId} shouldRefresh={shouldRefresh} />
        ) : null}
      </div>
    </CustomModal>
  );
};
